import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import { filter, pipe } from 'remeda';
import { PRODUCTS_BY_ID } from '../../constants/products';
import type { Users } from '../../types/database';

type UnknownRecord = Record<string, unknown>;

export type AdminUserListRecord = Users & UnknownRecord;

export type AdminSubscriptionStatus =
  | 'active'
  | 'expiringSoon'
  | 'inactive'
  | 'expired'
  | 'trial'
  | 'cancelled'
  | 'unknown';

export interface AdminSubscriptionSummary {
  productId: number | null;
  name: string;
  category: string | null;
  status: AdminSubscriptionStatus;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  image: string | null;
  source: string | null;
}

export interface AdminGeolocationSummary {
  label: string;
  city: string | null;
  region: string | null;
  country: string | null;
  timezone: string | null;
  ipAddress: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface EnrichedAdminUser extends AdminUserListRecord {
  normalizedSubscriptions: AdminSubscriptionSummary[];
  primarySubscription: AdminSubscriptionSummary | null;
  subscriptionTypes: string[];
  subscriptionVisibilityStatus: 'active' | 'expiringSoon' | 'expired' | 'none';
  geolocationSummary: AdminGeolocationSummary | null;
  displayName: string;
  devicesCount: number;
  coursesCount: number;
}

export type AdminUsersAssetsFilter =
  | 'all'
  | 'devices'
  | 'devicesOnly'
  | 'courses'
  | 'coursesOnly'
  | 'both';

/** Canonical Users.legacy_source values shown as badges in the directory. */
export const KNOWN_LEGACY_SOURCE_TAGS = [
  'v0',
  'v0-new',
  'v0-partial',
  'v1',
  'v1-new',
  'v1-partial',
] as const;

export type AdminUsersFilterState = {
  search: string;
  subscriptionStatuses: EnrichedAdminUser['subscriptionVisibilityStatus'][];
  subscriptionTypes: string[];
  roles: string[];
  accountTypes: string[];
  geolocation: 'all' | 'available' | 'missing';
  /** Exact `legacy_source` tags; empty = no provenance filter. */
  legacySources: string[];
  assets: AdminUsersAssetsFilter;
};

export const DEFAULT_SUBSCRIPTION_FILTERS: EnrichedAdminUser['subscriptionVisibilityStatus'][] =
  ['active', 'expiringSoon', 'expired', 'none'];

export const EXPIRING_SOON_MONTHS = 2;

const SUBSCRIPTION_PRODUCT_IDS = new Set(
  Object.keys(PRODUCTS_BY_ID).map(Number)
);

const SUBSCRIPTION_COLLECTION_KEYS = [
  'subscriptions',
  'subscription',
  'userProducts',
  'user_products',
  'products',
  'memberships',
  'plans',
  'licenses',
  'entitlements',
  'activeSubscriptions',
  'inactiveSubscriptions',
] as const;

const GEO_KEYS = ['geolocation', 'geo', 'location', 'localization'] as const;

const asRecord = (value: unknown): UnknownRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const readValue = (record: UnknownRecord, keys: string[]): unknown => {
  for (const key of keys) {
    if (
      record[key] !== undefined &&
      record[key] !== null &&
      record[key] !== ''
    ) {
      return record[key];
    }
  }

  return undefined;
};

const readString = (record: UnknownRecord, keys: string[]): string | null => {
  const value = readValue(record, keys);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const readNumber = (record: UnknownRecord, keys: string[]): number | null => {
  const value = readValue(record, keys);

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const readBoolean = (record: UnknownRecord, keys: string[]): boolean | null => {
  const value = readValue(record, keys);

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['true', '1', 'active', 'enabled'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'inactive', 'disabled'].includes(normalized)) {
      return false;
    }
  }

  return null;
};

const normalizeDate = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const isSubscriptionLike = (entry: UnknownRecord): boolean => {
  const productId = readNumber(entry, ['product_id', 'productId', 'id']);
  const category = readString(entry, ['category', 'sub_category', 'type']);
  const name = readString(entry, ['name', 'product_name', 'title']);

  return Boolean(
    (productId !== null && SUBSCRIPTION_PRODUCT_IDS.has(productId)) ||
      category?.toLowerCase().includes('life') ||
      name?.toLowerCase().includes('augmented')
  );
};

const normalizeStatus = (
  candidate: string | null,
  isActive: boolean | null,
  endDate: string | null
): AdminSubscriptionStatus => {
  const normalized = candidate?.toLowerCase();

  if (normalized) {
    if (normalized.includes('trial')) return 'trial';
    if (normalized.includes('cancel')) return 'cancelled';
    if (
      normalized.includes('active') ||
      normalized.includes('valid') ||
      normalized.includes('current')
    ) {
      return 'active';
    }
    if (normalized.includes('expired') || normalized.includes('ended')) {
      return 'expired';
    }
    if (normalized.includes('inactive') || normalized.includes('unactive')) {
      return 'inactive';
    }
  }

  if (isActive === true) {
    return 'active';
  }

  if (isActive === false) {
    return 'inactive';
  }

  if (endDate && isBefore(new Date(endDate), new Date())) {
    return 'expired';
  }

  return endDate || candidate ? 'active' : 'unknown';
};

const isExpiringSoonDate = (
  value: string | null | undefined,
  thresholdMonths: number = EXPIRING_SOON_MONTHS
): boolean => {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const now = new Date();
  const thresholdDate = addMonths(now, thresholdMonths);

  return isAfter(date, now) && isBefore(date, thresholdDate);
};

const collectSubscriptionCandidates = (
  user: AdminUserListRecord
): unknown[] => {
  const candidates = SUBSCRIPTION_COLLECTION_KEYS.flatMap(key =>
    asArray(user[key])
  );

  if (candidates.length > 0) {
    return candidates;
  }

  const flatCandidate = {
    product_id: readNumber(user, [
      'subscription_product_id',
      'subscriptionProductId',
      'product_id',
    ]),
    name: readString(user, ['subscription_type', 'subscription_name']),
    category: readString(user, ['subscription_category']),
    status: readString(user, ['subscription_status']),
    start_date: readString(user, [
      'subscription_start',
      'subscription_start_date',
    ]),
    until_date: readString(user, ['subscription_end', 'subscription_end_date']),
    short_description: readString(user, ['subscription_description']),
    image: readString(user, ['subscription_image']),
  };

  return isSubscriptionLike(flatCandidate) ? [flatCandidate] : [];
};

export const extractUserSubscriptions = (
  user: AdminUserListRecord
): AdminSubscriptionSummary[] => {
  return collectSubscriptionCandidates(user)
    .map(item => asRecord(item))
    .filter((entry): entry is UnknownRecord => Boolean(entry))
    .filter(isSubscriptionLike)
    .map(entry => {
      const productId = readNumber(entry, ['product_id', 'productId', 'id']);
      const productDefinition =
        productId !== null ? PRODUCTS_BY_ID[productId] : undefined;
      const startDate = normalizeDate(
        readString(entry, [
          'start_date',
          'startDate',
          'created_at',
          'create_time',
          'from_date',
        ])
      );
      const endDate = normalizeDate(
        readString(entry, ['until_date', 'expires_at', 'end_date', 'endDate'])
      );
      const activeFlag = readBoolean(entry, ['is_active', 'active', 'enabled']);

      return {
        productId,
        name:
          readString(entry, ['name', 'product_name', 'title']) ??
          productDefinition?.name ??
          'Subscription',
        category:
          readString(entry, ['category', 'sub_category', 'type']) ??
          productDefinition?.category ??
          null,
        status: normalizeStatus(
          readString(entry, ['status', 'state']),
          activeFlag,
          endDate
        ),
        startDate,
        endDate,
        description: readString(entry, [
          'short_description',
          'description',
          'tooltip',
        ]),
        image: readString(entry, ['image', 'icon', 'logo']),
        source: readString(entry, ['source', 'provider']),
      };
    })
    .sort((left, right) => {
      const leftPriority =
        left.status === 'active' || left.status === 'trial'
          ? 0
          : left.status === 'expired'
            ? 1
            : 2;
      const rightPriority =
        right.status === 'active' || right.status === 'trial'
          ? 0
          : right.status === 'expired'
            ? 1
            : 2;

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return (right.endDate ?? '').localeCompare(left.endDate ?? '');
    });
};

export const extractGeolocationSummary = (
  user: AdminUserListRecord
): AdminGeolocationSummary | null => {
  const nestedGeo =
    GEO_KEYS.map(key => asRecord(user[key])).find(Boolean) ?? null;
  const source = nestedGeo ?? user;

  const city = readString(source, ['city', 'town']);
  const region =
    readString(source, ['region', 'state', 'province']) ?? user.region ?? null;
  const country = readString(source, ['country', 'country_code']);
  const timezone = readString(source, ['timezone', 'tz']);
  const ipAddress = readString(source, ['ip_address', 'ip']);
  const latitude = readNumber(source, ['latitude', 'lat']);
  const longitude = readNumber(source, ['longitude', 'lng', 'lon']);
  const address =
    readString(source, ['address', 'formatted_address']) ??
    user.address ??
    null;

  const parts = [city, region, country].filter(Boolean);
  const label = parts.join(', ') || address || timezone || ipAddress;

  if (!label && latitude === null && longitude === null) {
    return null;
  }

  return {
    label: label || 'Geolocation available',
    city,
    region,
    country,
    timezone,
    ipAddress,
    latitude,
    longitude,
  };
};

const getPrimarySubscription = (
  subscriptions: AdminSubscriptionSummary[]
): AdminSubscriptionSummary | null => subscriptions[0] ?? null;

const getVisibilityStatus = (
  subscriptions: AdminSubscriptionSummary[]
): EnrichedAdminUser['subscriptionVisibilityStatus'] => {
  if (
    subscriptions.some(
      item =>
        (item.status === 'active' || item.status === 'trial') &&
        isExpiringSoonDate(item.endDate)
    )
  ) {
    return 'expiringSoon';
  }

  if (
    subscriptions.some(
      item => item.status === 'active' || item.status === 'trial'
    )
  ) {
    return 'active';
  }

  if (subscriptions.some(item => item.status === 'expired')) {
    return 'expired';
  }

  if (subscriptions.length > 0) {
    return 'expired';
  }

  return 'none';
};

const getDisplayName = (user: Users): string => {
  const name =
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ').trim();

  return name || user.email || `User #${user.id}`;
};

const parseNonNegativeCount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return Math.floor(parsed);
    }
  }
  return 0;
};

const getDevicesCount = (user: AdminUserListRecord): number =>
  parseNonNegativeCount(
    user.devices_count ?? (user as UnknownRecord).devicesCount
  );

const getCoursesCount = (user: AdminUserListRecord): number =>
  parseNonNegativeCount(
    user.courses_count ?? (user as UnknownRecord).coursesCount
  );

export const matchesLegacySources = (
  legacySource: string | null | undefined,
  selectedSources: string[]
): boolean => {
  if (selectedSources.length === 0) {
    return true;
  }
  if (legacySource == null || String(legacySource).trim() === '') {
    return false;
  }
  const normalized = String(legacySource).trim().toLowerCase();
  return selectedSources.some(
    selected => selected.trim().toLowerCase() === normalized
  );
};

export const matchesAssetsFilter = (
  devicesCount: number,
  coursesCount: number,
  assets: AdminUsersAssetsFilter
): boolean => {
  if (assets === 'all') {
    return true;
  }
  if (assets === 'devices') {
    return devicesCount > 0;
  }
  if (assets === 'devicesOnly') {
    return devicesCount > 0 && coursesCount === 0;
  }
  if (assets === 'courses') {
    return coursesCount > 0;
  }
  if (assets === 'coursesOnly') {
    return coursesCount > 0 && devicesCount === 0;
  }
  return devicesCount > 0 && coursesCount > 0;
};

export const enrichAdminUsers = (
  users: AdminUserListRecord[]
): EnrichedAdminUser[] =>
  users.map(user => {
    const normalizedSubscriptions = extractUserSubscriptions(user);
    const primarySubscription = getPrimarySubscription(normalizedSubscriptions);

    return {
      ...user,
      normalizedSubscriptions,
      primarySubscription,
      subscriptionTypes: normalizedSubscriptions.map(item => item.name),
      subscriptionVisibilityStatus: getVisibilityStatus(
        normalizedSubscriptions
      ),
      geolocationSummary: extractGeolocationSummary(user),
      displayName: getDisplayName(user),
      devicesCount: getDevicesCount(user),
      coursesCount: getCoursesCount(user),
    };
  });

export const formatAdminDate = (value: string | null | undefined): string =>
  value ? format(new Date(value), 'dd/MM/yyyy') : '-';

export const getSubscriptionStatusLabel = (
  status:
    | AdminSubscriptionStatus
    | EnrichedAdminUser['subscriptionVisibilityStatus']
): string => {
  switch (status) {
    case 'active':
      return 'Attiva';
    case 'expiringSoon':
      return 'In scadenza';
    case 'expired':
      return 'Scaduta';
    case 'trial':
      return 'Trial';
    case 'cancelled':
      return 'Cancellata';
    default:
      return 'Nessuna';
  }
};

export const getSubscriptionStatusColor = (
  status:
    | AdminSubscriptionStatus
    | EnrichedAdminUser['subscriptionVisibilityStatus']
): string => {
  switch (status) {
    case 'active':
      return 'teal';
    case 'expiringSoon':
      return 'orange';
    case 'trial':
      return 'blue';
    case 'expired':
      return 'red';
    case 'cancelled':
      return 'red';
    default:
      return 'dark';
  }
};

export const getEffectiveSubscriptionStatus = (
  subscription: Pick<AdminSubscriptionSummary, 'status' | 'endDate'>
): AdminSubscriptionStatus => {
  if (
    (subscription.status === 'active' || subscription.status === 'trial') &&
    isExpiringSoonDate(subscription.endDate)
  ) {
    return 'expiringSoon';
  }

  if (
    subscription.status === 'inactive' ||
    subscription.status === 'cancelled'
  ) {
    return 'expired';
  }

  return subscription.status;
};

export const summarizeSubscriptions = (users: EnrichedAdminUser[]) => {
  const totalUsers = users.length;
  const activeUsers = users.filter(
    user => user.subscriptionVisibilityStatus === 'active'
  ).length;
  const expiringSoonUsers = users.filter(
    user => user.subscriptionVisibilityStatus === 'expiringSoon'
  ).length;
  const expiredUsers = users.filter(
    user => user.subscriptionVisibilityStatus === 'expired'
  ).length;
  const geolocatedUsers = users.filter(user =>
    Boolean(user.geolocationSummary)
  ).length;

  return {
    totalUsers,
    activeUsers,
    expiringSoonUsers,
    expiredUsers,
    geolocatedUsers,
  };
};

export const getSubscriptionBreakdownData = (users: EnrichedAdminUser[]) => [
  {
    label: 'Attive',
    count: users.filter(user => user.subscriptionVisibilityStatus === 'active')
      .length,
  },
  {
    label: 'In scadenza',
    count: users.filter(
      user => user.subscriptionVisibilityStatus === 'expiringSoon'
    ).length,
  },
  {
    label: 'Scadute',
    count: users.filter(user => user.subscriptionVisibilityStatus === 'expired')
      .length,
  },
];

export const getSubscriptionTimelineData = (users: EnrichedAdminUser[]) => {
  const subscriptionDates = users
    .flatMap(user =>
      user.normalizedSubscriptions.map(
        subscription => subscription.startDate ?? subscription.endDate
      )
    )
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value))
    .filter(date => !Number.isNaN(date.getTime()))
    .sort((left, right) => left.getTime() - right.getTime());

  if (subscriptionDates.length === 0) {
    return Array.from({ length: 6 }, (_, index) => {
      const monthStart = startOfMonth(subMonths(new Date(), 5 - index));

      return {
        month: format(monthStart, 'MMM yy'),
        total: 0,
      };
    });
  }

  const firstMonth = startOfMonth(subscriptionDates[0]);
  const lastMonth = startOfMonth(
    subscriptionDates[subscriptionDates.length - 1]
  );
  const monthStarts: Date[] = [];

  for (
    let monthStart = firstMonth;
    monthStart <= lastMonth;
    monthStart = addMonths(monthStart, 1)
  ) {
    monthStarts.push(monthStart);
  }

  const subscriptions = users.flatMap(user => user.normalizedSubscriptions);

  return monthStarts.map(monthStart => {
    const monthEnd = endOfMonth(monthStart);
    const total = subscriptions.filter(subscription => {
      const referenceDate = subscription.startDate ?? subscription.endDate;

      if (!referenceDate) {
        return false;
      }

      const parsedDate = new Date(referenceDate);
      return !Number.isNaN(parsedDate.getTime()) && parsedDate <= monthEnd;
    }).length;

    return {
      month: format(monthStart, 'MMM yy'),
      total,
    };
  });
};

export const getTopSubscriptionPlansData = (users: EnrichedAdminUser[]) =>
  Array.from(
    users.reduce((accumulator, user) => {
      user.normalizedSubscriptions.forEach(subscription => {
        accumulator.set(
          subscription.name,
          (accumulator.get(subscription.name) ?? 0) + 1
        );
      });
      return accumulator;
    }, new Map<string, number>())
  )
    .map(([plan, count]) => ({ plan, count }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 6);

export const getTopLocationsData = (users: EnrichedAdminUser[]) =>
  Array.from(
    users.reduce((accumulator, user) => {
      const key = user.geolocationSummary?.label;
      if (!key) {
        return accumulator;
      }

      accumulator.set(key, (accumulator.get(key) ?? 0) + 1);
      return accumulator;
    }, new Map<string, number>())
  )
    .map(([location, count]) => ({ location, count }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 6);

export const getUniqueOptions = (
  values: Array<string | null | undefined>
): string[] =>
  pipe(
    values,
    filter((value): value is string => Boolean(value?.trim())),
    valuesList =>
      Array.from(new Set(valuesList)).sort((left, right) =>
        left.localeCompare(right)
      )
  );

export const applyAdminUsersFilters = (
  users: EnrichedAdminUser[],
  filters: AdminUsersFilterState
): EnrichedAdminUser[] => {
  const normalizedSearch = filters.search.trim().toLowerCase();

  return users.filter(user => {
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [
        String(user.id),
        user.displayName,
        user.email,
        user.role,
        user.type,
        user.geolocationSummary?.label,
        ...user.subscriptionTypes,
      ]
        .filter(Boolean)
        .some(value => value?.toLowerCase().includes(normalizedSearch));

    const matchesSubscriptionStatus =
      filters.subscriptionStatuses.length === 0 ||
      filters.subscriptionStatuses.includes(user.subscriptionVisibilityStatus);

    const matchesSubscriptionType =
      filters.subscriptionTypes.length === 0 ||
      user.subscriptionTypes.some(subscriptionType =>
        filters.subscriptionTypes.includes(subscriptionType)
      );

    const matchesRole =
      filters.roles.length === 0 || filters.roles.includes(user.role ?? '');

    const matchesAccountType =
      filters.accountTypes.length === 0 ||
      filters.accountTypes.includes(user.type ?? '');

    const matchesGeolocation =
      filters.geolocation === 'all' ||
      (filters.geolocation === 'available' &&
        Boolean(user.geolocationSummary)) ||
      (filters.geolocation === 'missing' && !user.geolocationSummary);

    const matchesLegacySourcesFilter = matchesLegacySources(
      user.legacy_source,
      filters.legacySources
    );

    const matchesAssets = matchesAssetsFilter(
      user.devicesCount,
      user.coursesCount,
      filters.assets
    );

    return (
      matchesSearch &&
      matchesSubscriptionStatus &&
      matchesSubscriptionType &&
      matchesRole &&
      matchesAccountType &&
      matchesGeolocation &&
      matchesLegacySourcesFilter &&
      matchesAssets
    );
  });
};

const parseUserCreatedAt = (user: EnrichedAdminUser): Date | null => {
  const raw =
    user.created_at ??
    (user as UnknownRecord).createdAt ??
    (user as UnknownRecord).create_time;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export type UserGrowthRange = '30d' | '6m' | 'all';

export const getUserGrowthData = (
  users: EnrichedAdminUser[],
  range: UserGrowthRange = '6m'
) => {
  const createdAtDates = users
    .map(parseUserCreatedAt)
    .filter((date): date is Date => date != null)
    .sort((left, right) => left.getTime() - right.getTime());

  if (range === '30d') {
    const today = startOfDay(new Date());
    const dayStarts = Array.from({ length: 30 }, (_, index) =>
      startOfDay(subDays(today, 29 - index))
    );

    return dayStarts.map(dayStart => {
      const nextDay = addDays(dayStart, 1);
      const count = createdAtDates.filter(
        createdAt => createdAt >= dayStart && createdAt < nextDay
      ).length;

      return {
        month: format(dayStart, 'dd MMM'),
        users: count,
      };
    });
  }

  const lastMonth = startOfMonth(new Date());
  let firstMonth =
    createdAtDates.length > 0
      ? startOfMonth(createdAtDates[0])
      : startOfMonth(subMonths(lastMonth, 5));

  if (range === '6m') {
    const windowStart = startOfMonth(subMonths(lastMonth, 5));
    if (firstMonth < windowStart || createdAtDates.length === 0) {
      firstMonth = windowStart;
    }
  }

  const monthStarts: Date[] = [];
  for (
    let monthStart = firstMonth;
    monthStart <= lastMonth;
    monthStart = addMonths(monthStart, 1)
  ) {
    monthStarts.push(monthStart);
  }

  if (monthStarts.length === 0) {
    monthStarts.push(lastMonth);
  }

  return monthStarts.map(monthStart => {
    const nextMonth = startOfMonth(addMonths(monthStart, 1));
    const count = createdAtDates.filter(
      createdAt => createdAt >= monthStart && createdAt < nextMonth
    ).length;

    return {
      month: format(monthStart, 'MMM yy'),
      users: count,
    };
  });
};
